/**
 * Device Service
 * Handles device registration and room assignment with the backend
 */

import { apiClient } from '@/shared/utils/api';
import { getDeviceFingerprint } from '@/utils/deviceFingerprint';
import { BACKEND_CONFIG } from '@/config/backendConfig';

export interface DeviceRoom {
  room_id: string;
  room_name: string;
  room_number: string;
  department_id: string | null;
  department_name?: string;
}

export interface DeviceInfo {
  device_id: string;
  device_fingerprint: string;
  device_name: string | null;
  room: DeviceRoom | null;
  registered_at?: string;
  is_new?: boolean;
}

export interface DeviceRegistration {
  device_fingerprint: string;
  room_id?: string | null;
  device_name?: string;
  user_agent?: string;
}

class DeviceService {
  /**
   * Register or update a device with the backend
   */
  async registerDevice(data: DeviceRegistration): Promise<DeviceInfo> {
    try {
      const response = await apiClient.post<DeviceInfo>('/facilities/devices/register/', data);
      return response.data;
    } catch (error) {
      console.error('Failed to register device:', error);
      throw error;
    }
  }

  /**
   * Get room assignment for the current device
   */
  async getDeviceRoom(fingerprint?: string): Promise<DeviceInfo | null> {
    try {
      const deviceFingerprint = fingerprint || await getDeviceFingerprint();
      const endpoint = BACKEND_CONFIG.endpoints.deviceRoom(BACKEND_CONFIG.isFastAPI);
      const response = await apiClient.get<DeviceInfo>(endpoint, {
        params: { fingerprint: deviceFingerprint }
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Device not registered yet
        return null;
      }
      console.error('Failed to get device room:', error);
      throw error;
    }
  }

  /**
   * Get room assignment based on IP address (cross-browser)
   */
  async getRoomByIP(): Promise<DeviceInfo | null> {
    try {
      const endpoint = BACKEND_CONFIG.endpoints.deviceRoomByIP(BACKEND_CONFIG.isFastAPI);
      const response = await apiClient.get<DeviceInfo>(endpoint);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        // No device registered for this IP
        return null;
      }
      console.error('Failed to get room by IP:', error);
      throw error;
    }
  }

  /**
   * Update room assignment for the current device
   */
  async updateRoomAssignment(roomId: string | null, deviceName?: string): Promise<DeviceInfo> {
    try {
      const deviceFingerprint = await getDeviceFingerprint();
      const response = await apiClient.post<DeviceInfo>('/facilities/devices/register/', {
        device_fingerprint: deviceFingerprint,
        room_id: roomId,
        device_name: deviceName,
        user_agent: navigator.userAgent,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update room assignment:', error);
      throw error;
    }
  }

  /**
   * List all registered devices (admin only)
   */
  async listDevices(): Promise<DeviceInfo[]> {
    try {
      const response = await apiClient.get<{ devices: DeviceInfo[] }>('/facilities/devices/');
      return response.data.devices;
    } catch (error) {
      console.error('Failed to list devices:', error);
      throw error;
    }
  }

  /**
   * Delete (deactivate) a device (admin only)
   */
  async deleteDevice(deviceId: string): Promise<void> {
    try {
      await apiClient.delete(`/facilities/devices/${deviceId}/`);
    } catch (error) {
      console.error('Failed to delete device:', error);
      throw error;
    }
  }

  /**
   * Get current device fingerprint
   */
  async getCurrentFingerprint(): Promise<string> {
    return getDeviceFingerprint();
  }
}

export const deviceService = new DeviceService();
export default deviceService;
